import React, { useState, useEffect, useRef } from "react";
import {
	Form,
	Input,
	Select,
	Button,
	Upload,
	Row,
	Col,
	Dropdown,
	Popover,
	message,
	Modal,
	Checkbox,
} from "antd";
import {
	RightOutlined,
	DownOutlined,
	EditOutlined,
	LoadingOutlined,
} from "@ant-design/icons";
import {
	useHistory,
	useLocation,
} from "react-router-dom/cjs/react-router-dom.min";
import styled from "styled-components";
import SendAnnouncementModal from "./SendAnnouncementModal";
import { Info } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { getClientid, getLocations, getRoleList, getUserInfo } from "redux/selectors";
import { CALL_API } from "common/API";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";

// ── Quill font-size registration ─────────────────────────────────────────────
const Size = Quill.import("attributors/style/size");
Size.whitelist = ["10px", "11px", "12px", "13px", "14px", "15px", "16px", "17px", "18px", "19px", "20px"];
Quill.register(Size, true);

// ── Global Quill styles ───────────────────────────────────────────────────────
const quillStyles = `
  .ql-container.ql-snow { border: none !important; }
  .ql-toolbar.ql-snow   { display: none !important; }
  .ql-editor {
    min-height: 168px;
    max-height: 320px;
    overflow-y: auto;
    padding: 14px 16px !important;
    font-size: 14px;
    line-height: 1.7;
  }
  .ql-editor.ql-blank::before {
    font-style: normal !important;
    color: #bfbfbf;
  }
`;

// ── Styled components ─────────────────────────────────────────────────────────
const Wrapper = styled.div`
  .breadcrumb {
    display: flex;
    align-items: center;
    margin-bottom: 16px;
  }
  .lite-text {
    cursor: pointer;
    font-weight: 400;
    font-size: 16px;
    color: #636980;
  }
  padding-bottom: 80px;
`;

const FixedFooter = styled.div`
  position: fixed;
  bottom: 0;
  left: ${({ sidebarWidth }) => sidebarWidth || 290}px;
  right: 0;
  z-index: 1000;
//   background: #ffffff;
//   box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.08);
  padding: 14px 32px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const SelectedCount = styled.span`
  font-size: 13px;
  color: #8c8c8c;
  font-weight: 400;
`;

const PolicyList = styled.div`
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  overflow: hidden;
  max-height: 320px;
  overflow-y: auto;
`;

const PolicyItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: background 0.15s;
  &:last-child { border-bottom: none; }
  &:hover { background: #f9fafb; }
`;

const ModalFooterBtns = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
`;

const BtnCancel = styled.button`
  padding: 8px 28px;
  border-radius: 8px;
  border: 1.5px solid #262626;
  background: #fff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  color: #262626;
  &:hover { background: #f5f5f5; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const BtnConfirm = styled.button`
  padding: 8px 28px;
  border-radius: 8px;
  border: none;
  background: #b7f0d8;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  color: #262626;
  &:hover { background: #9de8cb; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const EditorWrapper = styled.div`
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  overflow: hidden;
  transition: border-color 0.2s, box-shadow 0.2s;
  background: #fff;
  &:focus-within {
    border-color: #4096ff;
    box-shadow: 0 0 0 2px rgba(64, 150, 255, 0.1);
  }
`;

const EditorToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-bottom: 1px solid #f0f0f0;
  background: #fafafa;
`;

const ToolbarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const ToolbarBtn = styled.button`
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  color: #595959;
  font-weight: 500;
  transition: background 0.15s;
  padding: 0;
  &:hover { background: #e8e8e8; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const ToolbarDivider = styled.div`
  width: 1px;
  height: 16px;
  background: #e0e0e0;
  margin: 0 6px;
`;

const FontSizeControl = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: 13px;
  color: #595959;
`;

const AttachBtn = styled.a`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #6480e2;
  font-size: 13px;
  font-weight: 500;
  text-decoration: none !important;
  padding: 5px 12px;
  border-radius: 6px;
  cursor: pointer;
`;

const FileChip = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  background: #ffffff;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  font-size: 13px;
  max-width: 200px;
  min-width: 100px;
`;

const FileChipName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #262626;
  font-size: 13px;
  font-weight: 500;
  cursor: ${({ loading }) => (loading ? "not-allowed" : "pointer")};
`;

const FileChipRemove = styled.span`
  cursor: pointer;
  color: #bfbfbf;
  font-weight: 700;
  font-size: 15px;
  line-height: 1;
  flex-shrink: 0;
  transition: color 0.2s;
  &:hover { color: #ff4d4f; }
`;

// ── SVG icons ─────────────────────────────────────────────────────────────────
const PaperclipIcon = ({ size = 14, color = "#2D53DA" }) => (
	<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
		<path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66L9.41 17.41a2 2 0 01-2.83-2.83l8.49-8.48" />
	</svg>
);

const OlIcon = () => (
	<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
		<line x1="10" y1="6" x2="21" y2="6" /><line x1="10" y1="12" x2="21" y2="12" /><line x1="10" y1="18" x2="21" y2="18" />
		<text x="2" y="8" fontSize="7" fill="currentColor" stroke="none">1</text>
		<text x="2" y="14" fontSize="7" fill="currentColor" stroke="none">2</text>
		<text x="2" y="20" fontSize="7" fill="currentColor" stroke="none">3</text>
	</svg>
);

const UlIcon = () => (
	<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
		<line x1="10" y1="6" x2="21" y2="6" /><line x1="10" y1="12" x2="21" y2="12" /><line x1="10" y1="18" x2="21" y2="18" />
		<circle cx="4" cy="6" r="1.5" fill="currentColor" stroke="none" />
		<circle cx="4" cy="12" r="1.5" fill="currentColor" stroke="none" />
		<circle cx="4" cy="18" r="1.5" fill="currentColor" stroke="none" />
	</svg>
);

// ── Component ─────────────────────────────────────────────────────────────────
const FONT_SIZES = ["10", "11", "12", "13", "14", "15", "16", "17", "18", "19", "20"];

const AddEditAnnouncement = () => {
	const [form] = Form.useForm();
	const history = useHistory();
	const location = useLocation();
	const quillRef = useRef(null);

	const editData = location?.state?.editData || null;
	const isEditMode = !!editData;

	// ── State ──────────────────────────────────────────────────────────────────
	const [titleValue, setTitleValue] = useState("");
	const [titleError, setTitleError] = useState(false);
	const [description, setDescription] = useState("");
	const [announcementModalOpen, setAnnouncementModalOpen] = useState(false);
	const [selectedSummary, setSelectedSummary] = useState([]);
	const [sendType, setSendType] = useState("all");
	const [selectedRoleIds, setSelectedRoleIds] = useState([]);
	const [selectedLocationIds, setSelectedLocationIds] = useState([]);
	const [loadingType, setLoadingType] = useState(null);
	const [attachedFiles, setAttachedFiles] = useState([]);
	const [hrModalOpen, setHrModalOpen] = useState(false);
	const [selectedPolicies, setSelectedPolicies] = useState([]);
	const [tempSelectedPolicies, setTempSelectedPolicies] = useState([]);
	const [hrPolicies, setHrPolicies] = useState([]);
	const [previewLoading, setPreviewLoading] = useState(null);
	const [publishModalOpen, setPublishModalOpen] = useState(false);
	const [sendEmailOnUpdate, setSendEmailOnUpdate] = useState(false);
	const [fontSize, setFontSize] = useState(4); // default index 4 = "14"

	// ── Selectors ──────────────────────────────────────────────────────────────
	const roleList = useSelector(getRoleList);
	const roleOptions = roleList?.filter((role) => role?.display === 1);
	const locationList = useSelector(getLocations) || [];
	const userInfo = useSelector(getUserInfo);
	const clientId = useSelector(getClientid);
	const userRoleIds = (userInfo?.role_id || []).map(String);

	const visibleHrPolicies = hrPolicies.filter((policy) => {
		const policyRoleIds = (policy?.roleId || []).map(String);
		if (policyRoleIds?.length === 0) return true;
		return policyRoleIds.some((id) => userRoleIds.includes(id));
	});

	// ── Effects ────────────────────────────────────────────────────────────────
	useEffect(() => { fetchHrPolicies(); }, []);

	useEffect(() => {
		if (isEditMode && editData) {
			setTitleValue(editData?.title || "");
			form.setFieldsValue({
				level: editData?.level || "General",
			});
			setDescription(editData?.announcement_description || "");

			let documents = editData?.attacheddocument;
			if (typeof documents === "string") {
				try { documents = JSON.parse(documents); } catch (e) { documents = []; }
			}
			if (Array.isArray(documents) && documents?.length > 0) {
				setAttachedFiles(
					documents?.map((doc, i) => ({
						uid: `existing-${i}`,
						name: doc.fileName || doc.name || `File ${i + 1}`,
						existing: true,
						isHrPolicy: doc.isHrPolicy || false,
						policyId: doc.policyId ? String(doc.policyId) : undefined,
						attachmentDoc: {
							documentId: doc.documentId,
							objectKey: doc.objectKey,
							fileName: doc.fileName || doc.name,
							fileType: doc.fileType,
							fileSize: doc.fileSize,
							documentPath: doc.documentPath,
						},
					}))
				);
			}
			const existingPolicyIds = documents
				?.filter((doc) => doc.isHrPolicy && doc.policyId)
				?.map((doc) => String(doc.policyId)) || [];
			if (existingPolicyIds?.length > 0) setSelectedPolicies(existingPolicyIds);

			const roleIds = editData?.roleid || editData?.roleId || [];
			const locationIds = editData?.locationid || editData?.locationId || [];
			if (roleIds?.length > 0 || locationIds?.length > 0) {
				setSendType("specific");
				setSelectedRoleIds(roleIds);
				setSelectedLocationIds(locationIds);
			}
		}
	}, [isEditMode, editData, form]);

	useEffect(() => {
		if (!isEditMode || !editData) return;
		const roleIds = editData?.roleid || editData?.roleId || [];
		const locationIds = editData?.locationid || editData?.locationId || [];
		if (
			(roleIds?.length > 0 || locationIds?.length > 0) &&
			(roleList?.length > 0 || locationList?.length > 0)
		) {
			const roleNames = roleIds.map(
				(id) => roleList?.find((r) => String(r.roleId) === String(id))?.roleName || id
			);
			const locationNames = locationIds.map(
				(id) => locationList?.find((l) => String(l.location_id) === String(id))?.location_name || id
			);
			setSelectedSummary([...roleNames, ...locationNames]);
		}
	}, [isEditMode, editData, roleList, locationList]);

	// ── Validation ─────────────────────────────────────────────────────────────
	const validateForm = () => {
		if (!titleValue?.trim()) {
			setTitleError(true);
			return false;
		}
		setTitleError(false);
		return true;
	};

	// ── HR Policies ────────────────────────────────────────────────────────────
	const openHrModal = () => {
		setTempSelectedPolicies(selectedPolicies);
		setHrModalOpen(true);
	};

	const fetchHrPolicies = async () => {
		try {
			const res = await CALL_API(`/hrpolicy/${clientId}`, "get");
			setHrPolicies(Array.isArray(res) ? res : res?.data || []);
		} catch (err) {
			message.error("Failed to fetch HR policies", err);
		}
	};

	const handleHrConfirm = () => {
		const filteredPrev = attachedFiles.filter(
			(f) => !f.isHrPolicy || tempSelectedPolicies.includes(f.policyId)
		);
		const newPolicyFiles = tempSelectedPolicies
			.filter((id) => !selectedPolicies.includes(id))
			.flatMap((id) => {
				const policy = hrPolicies.find((p) => String(p.policyId) === String(id));
				if (!policy) return [];
				if (!policy.policyDocument?.length) {
					return [{ uid: `policy-${id}-no-doc`, name: policy.policyTitle, isHrPolicy: true, policyId: id, attachmentDoc: null }];
				}
				return policy.policyDocument.map((doc) => ({
					uid: `policy-${id}-doc-${doc.documentId}`,
					name: doc.name,
					policyTitle: policy.policyTitle,
					isHrPolicy: true,
					policyId: id,
					attachmentDoc: {
						documentId: String(doc.documentId),
						objectKey: doc.s3BucketObjectName,
						fileName: doc.name,
						fileType: doc.type,
						fileSize: null,
						documentPath: doc.documentPath,
					},
				}));
			});
		setAttachedFiles([...filteredPrev, ...newPolicyFiles]);
		setSelectedPolicies(tempSelectedPolicies);
		setHrModalOpen(false);
	};

	const handleHrCancel = () => {
		setTempSelectedPolicies(selectedPolicies);
		setHrModalOpen(false);
	};

	const togglePolicy = (id) => {
		setTempSelectedPolicies((prev) =>
			prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
		);
	};

	// ── File preview ───────────────────────────────────────────────────────────
	const handlePreviewFile = async (fileEntry) => {
		if (fileEntry.isLocal && !fileEntry.isUploaded) {
			message.info("File will be available to preview after publishing");
			return;
		}
		if (fileEntry.isHrPolicy && !editData?.id) {
			message.info("File will be available to preview after publishing");
			return;
		}
		const documentId = String(fileEntry.attachmentDoc?.documentId || fileEntry.documentId || "");
		const announcementId = String(editData?.id || "1");
		if (!documentId || documentId === "undefined" || documentId === "null") {
			message.info("File will be available to preview after publishing");
			return;
		}
		try {
			setPreviewLoading(fileEntry.uid);
			const payload = {
				method: "preview-announcement-document",
				documentId: String(documentId),
				announcementId,
			};
			if (fileEntry.isHrPolicy === true && fileEntry.policyId) {
				payload.isPolicy = true;
				payload.policyId = String(fileEntry.policyId);
			}
			const res = await CALL_API(`document-list/${clientId}`, "post", payload);
			const previewUrl = res?.url || res?.previewUrl || res?.signedUrl || res?.data?.url;
			if (previewUrl) window.open(previewUrl, "_blank");
			else message.error("Preview URL not available");
		} catch {
			message.error("Failed to preview file");
		} finally {
			setPreviewLoading(null);
		}
	};

	const removeFile = (uid) => {
		const removed = attachedFiles.find((f) => f.uid === uid);
		if (removed?.isHrPolicy) setSelectedPolicies((prev) => prev.filter((id) => id !== removed.policyId));
		setAttachedFiles((prev) => prev.filter((f) => f.uid !== uid));
	};

	// ── Upload pending files ───────────────────────────────────────────────────
	const uploadAllPendingFiles = async () => {
		const updated = [...attachedFiles];
		const pendingEntries = updated
			.map((entry, i) => ({ ...entry, _index: i }))
			.filter((entry) => entry.isLocal && !entry.isUploaded);
		if (pendingEntries?.length === 0) return updated;

		const res = await CALL_API("insyts-user-dashboard/addannouncement?file", "post", {
			filenames: pendingEntries.map((e) => e.file.name),
		});
		const uploadFileData = res?.uploadFileData;
		if (!uploadFileData?.length) throw new Error("Could not get upload URLs from server");

		uploadFileData.forEach((fileRes, i) => {
			const file = pendingEntries[i].file;
			fetch(fileRes.presignedUrl, {
				method: "PUT",
				headers: { "Content-Type": file.type || "application/octet-stream" },
				body: file,
			}).catch((err) => message.error(`Background S3 upload failed for "${file.name}":`, err));
		});

		uploadFileData.forEach((fileRes, i) => {
			const entry = pendingEntries[i];
			const documentId = fileRes.fileKey?.split("_")?.[1] || null;
			updated[entry._index] = {
				...entry,
				isUploaded: true,
				attachmentDoc: {
					documentId,
					objectKey: fileRes.fileKey,
					fileName: entry.file.name,
					fileType: entry.file.type,
					fileSize: entry.file.size,
				},
			};
		});
		setAttachedFiles(updated);
		return updated;
	};

	// ── Payload builders ───────────────────────────────────────────────────────
	const buildAttachedDocuments = (files) =>
		files.map(({ name, isHrPolicy, policyId, attachmentDoc }) => {
			if (isHrPolicy && attachmentDoc) {
				return {
					documentId: attachmentDoc.documentId,
					fileName: attachmentDoc.fileName,
					fileSize: attachmentDoc.fileSize,
					fileType: attachmentDoc.fileType,
					objectKey: attachmentDoc.objectKey,
					policyId,
					isHrPolicy: true,
				};
			}
			if (isHrPolicy) return { name, policyId, isHrPolicy: true };
			if (attachmentDoc) return attachmentDoc;
			return { name };
		});

	const buildPayload = (files, status = "Published") => {
		const values = form.getFieldsValue();
		const payload = {
			title: titleValue,
			announcement_description: description,
			level: values.level,
			sendTo: sendType === "all" ? "all" : "specific",
			announcementstatus: status,
			createdby: userInfo?.userid,
			createdusername: userInfo?.name,
			attachedDocuments: buildAttachedDocuments(files),
		};
		if (sendType === "specific") {
			payload.roleId = selectedRoleIds;
			payload.locationId = selectedLocationIds;
		}
		return payload;
	};

	const buildUpdatePayload = (files, status = "Published") => {
		const values = form.getFieldsValue();
		return {
			id: editData?.id,
			title: titleValue,
			level: values.level,
			announcement_description: description,
			announcementstatus: status,
			locationId: selectedLocationIds,
			roleId: selectedRoleIds,
			attachedDocuments: buildAttachedDocuments(files),
			sendTo: sendType === "all" ? "all" : "specific",
			updatedby: userInfo?.userid,
			updatedusername: userInfo?.name,
		};
	};

	// ── API calls ──────────────────────────────────────────────────────────────
	const publishAnnouncement = async (isEmail = false, status = "Published") => {
		if (!validateForm()) return;
		const type = status === "Draft" ? "draft" : isEmail ? "email" : "publish";
		try {
			setLoadingType(type);
			const updatedFiles = await uploadAllPendingFiles();
			const payload = buildPayload(updatedFiles, status);
			const url = isEmail
				? "insyts-user-dashboard/addannouncement?isEmail=true"
				: "insyts-user-dashboard/addannouncement";
			const response = await CALL_API(url, "post", payload);
			message.success(
				status === "Draft"
					? "Announcement saved as draft"
					: isEmail
						? response?.message || "Announcement published & emailed successfully"
						: response?.message || "Announcement published successfully"
			);
			history.push("/announcement", { activeTab: "2" });
		} catch (error) {
			message.error("Failed to save announcement");
		} finally {
			setLoadingType(null);
		}
	};

	const updateAnnouncement = async (isEmail = false) => {
		if (!validateForm()) return;
		const type = isEmail ? "email" : "publish";
		try {
			setLoadingType(type);
			const updatedFiles = await uploadAllPendingFiles();
			const payload = buildUpdatePayload(updatedFiles, "Published");
			const url = isEmail
				? "insyts-user-dashboard/updateannouncement?isEmail=true"
				: "insyts-user-dashboard/updateannouncement";
			const response = await CALL_API(url, "patch", payload);
			message.success(
				isEmail
					? response?.message || "Announcement updated & emailed successfully"
					: response?.message || "Announcement updated successfully"
			);
			setPublishModalOpen(false);
			history.push("/announcement", { activeTab: "2" });
		} catch {
			message.error("Failed to update announcement");
		} finally {
			setLoadingType(null);
		}
	};

	const handleUpdateClick = () => {
		if (!validateForm()) return;
		setSendEmailOnUpdate(false);
		setPublishModalOpen(true);
	};

	const handleDraft = () => publishAnnouncement(false, "Draft");
	const handlePublishAndEmail = () => publishAnnouncement(true, "Published");

	// ── Quill formatting ───────────────────────────────────────────────────────
	const handleFormat = (format, value) => {
		const editor = quillRef.current?.getEditor();
		if (!editor) return;
		const selection = editor.getSelection();
		if (!selection) {
			const length = editor.getLength();
			editor.setSelection(length, 0);
			const current = editor.getFormat(length, 0);
			editor.format(format, value !== undefined ? (current[format] === value ? false : value) : !current[format]);
			return;
		}
		const current = editor.getFormat(selection.index, selection.length);
		editor.format(format, value !== undefined ? (current[format] === value ? false : value) : !current[format]);
	};

	const handleFontSizeDown = () => {
		const editor = quillRef.current?.getEditor();
		if (!editor) return;
		const newIndex = Math.max(0, fontSize - 1);
		setFontSize(newIndex);
		const sel = editor.getSelection() || { index: editor.getLength(), length: 0 };
		editor.setSelection(sel.index, sel.length);
		editor.format("size", FONT_SIZES[newIndex] + "px");
	};

	const handleFontSizeUp = () => {
		const editor = quillRef.current?.getEditor();
		if (!editor) return;
		const newIndex = Math.min(FONT_SIZES.length - 1, fontSize + 1);
		setFontSize(newIndex);
		const sel = editor.getSelection() || { index: editor.getLength(), length: 0 };
		editor.setSelection(sel.index, sel.length);
		editor.format("size", FONT_SIZES[newIndex] + "px");
	};

	const handleRemoveSummaryItem = (item) => {
		// Find if it's a role or location name
		const matchedRole = roleList?.find((r) => r.roleName === item);
		const matchedLocation = locationList?.find((l) => l.country === item);

		let newRoleIds = [...selectedRoleIds];
		let newLocationIds = [...selectedLocationIds];

		if (matchedRole) {
			newRoleIds = newRoleIds?.filter((id) => String(id) !== String(matchedRole.roleId));
			setSelectedRoleIds(newRoleIds);
		}
		if (matchedLocation) {
			// Remove all location entries with that country
			const countryLocationIds = locationList
				.filter((l) => l.country === item)
				.map((l) => String(l.location_id));
			newLocationIds = newLocationIds?.filter((id) => !countryLocationIds?.includes(String(id)));
			setSelectedLocationIds(newLocationIds);
		}

		const newSummary = selectedSummary?.filter((s) => s !== item);
		setSelectedSummary(newSummary);

		// If nothing left, reset to "all"
		if (newRoleIds?.length === 0 && newLocationIds?.length === 0) {
			setSendType("all");
		}
	};

	// ── Dropdown items ─────────────────────────────────────────────────────────
	const dropdownItems = [
		{
			key: "1",
			label: (
				<Upload
					showUploadList={false}
					accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif,.webp"
					beforeUpload={(file) => {
						const allowedTypes = [
							"application/pdf",
							"application/msword",
							"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
							"image/png",
							"image/jpeg",
							"image/gif",
							"image/webp",
						];
						if (!allowedTypes.includes(file.type)) {
							message.error("Only PDF, Word documents, and images are allowed");
							return Upload.LIST_IGNORE;
						}
						setAttachedFiles((prev) => [
							...prev,
							{ uid: file.uid, name: file.name, file, isLocal: true, isUploaded: false },
						]);
						return false;
					}}
					multiple
				>
					<div style={{ width: "100%", padding: "6px 12px", display: "flex", alignItems: "center" }}>
						From device
					</div>
				</Upload>
			),
		},
		{
			key: "2",
			label: (
				<div
					onClick={openHrModal}
					style={{ width: "100%", padding: "6px 12px", display: "flex", alignItems: "center", cursor: "pointer" }}
				>
					HR Policies
				</div>
			),
		},
	];

	// ── Render ─────────────────────────────────────────────────────────────────
	return (
		<Wrapper>
			<style>{quillStyles}</style>

			{/* ── HR Policies Modal ──────────────────────────────────────────── */}
			<Modal
				open={hrModalOpen}
				onCancel={handleHrCancel}
				footer={null}
				title="Attach HR Policies"
				width={520}
				bodyStyle={{ paddingTop: 8 }}
			>
				<SelectedCount>{tempSelectedPolicies?.length} Selected</SelectedCount>
				<PolicyList style={{ marginTop: 12 }}>
					{visibleHrPolicies?.length === 0 ? (
						<div style={{ padding: "20px", textAlign: "center", color: "#8c8c8c", fontSize: 14 }}>
							No policies available for your role
						</div>
					) : (
						visibleHrPolicies.map((policy) => (
							<PolicyItem key={policy.policyId} onClick={() => togglePolicy(String(policy.policyId))}>
								<Checkbox
									checked={tempSelectedPolicies.includes(String(policy.policyId))}
									onChange={() => togglePolicy(String(policy.policyId))}
									onClick={(e) => e.stopPropagation()}
								/>
								<span style={{ fontSize: 14, color: "#262626" }}>{policy.policyTitle}</span>
							</PolicyItem>
						))
					)}
				</PolicyList>
				<ModalFooterBtns>
					<BtnCancel onClick={handleHrCancel}>Cancel</BtnCancel>
					<BtnConfirm onClick={handleHrConfirm}>Confirm</BtnConfirm>
				</ModalFooterBtns>
			</Modal>

			{/* ── Publish Confirmation Modal ─────────────────────────────────── */}
			<Modal
				open={publishModalOpen}
				onCancel={() => { if (loadingType === null) setPublishModalOpen(false); }}
				footer={null}
				title={null}
				width={480}
				centered
				closable={loadingType === null}
				maskClosable={loadingType === null}
				styles={{
					content: { borderRadius: 16, padding: "32px 32px 24px" },
					body: { textAlign: "center" },
				}}
			>
				<div style={{ display: "flex", justifyContent: "center", marginBottom: 20 }}>
					<div style={{ width: 40, height: 40, borderRadius: "50%", background: "#ff1100", display: "flex", alignItems: "center", justifyContent: "center" }}>
						<span style={{ fontSize: 22, color: "#ffffff" }}>!</span>
					</div>
				</div>
				<p style={{ fontSize: 14, color: "#595959", lineHeight: 1.7, marginBottom: 20, textAlign: "center" }}>
					You are about to update and republish this announcement. This will replace the previous
					version, and the new content will be sent to all viewers. Once updated, the announcement
					will appear as the latest version and cannot be undone
				</p>
				<div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 28 }}>
					<Checkbox
						checked={sendEmailOnUpdate}
						onChange={(e) => setSendEmailOnUpdate(e.target.checked)}
					/>
					<span style={{ fontSize: 14, color: "#262626" }}>Do you wish to send an email notification?</span>
				</div>
				<ModalFooterBtns style={{ marginTop: 0 }}>
					<BtnCancel onClick={() => setPublishModalOpen(false)} disabled={loadingType !== null}>No</BtnCancel>
					<BtnConfirm onClick={() => updateAnnouncement(sendEmailOnUpdate)} disabled={loadingType !== null}>
						{loadingType !== null ? <LoadingOutlined /> : "Yes, Update"}
					</BtnConfirm>
				</ModalFooterBtns>
			</Modal>

			{/* ── Breadcrumb ─────────────────────────────────────────────────── */}
			<div className="breadcrumb">
				<p className="lite-text p-0 m-0" onClick={() => history.push("/announcement", { activeTab: "2" })}>
					Announcement
				</p>
				&nbsp;<RightOutlined className="mt-1" />&nbsp;
				<p className="m-0 p-0">{isEditMode ? "Editing Announcement" : "Adding Announcement"}</p>
			</div>

			<Form form={form} layout="vertical" initialValues={{ level: "General" }}>
				<Row gutter={24}>
					{/* ── Title — plain state, zero Form involvement ── */}
					<Col span={16}>
						<div style={{ marginBottom: 24 }}>
							<label style={{
								fontWeight: 600,
								color: titleError ? "#ff4d4f" : "#5d5f68",
								display: "block",
								marginBottom: 8,
							}}>
								<span style={{ color: "#ff4d4f", marginRight: 4 }}>*</span>Title
							</label>
							<Input
								value={titleValue}
								onChange={(e) => {
									setTitleValue(e.target.value);
									if (e.target.value) setTitleError(false);
								}}
								placeholder="Enter announcement title"
								status={titleError ? "error" : ""}
							/>
							{titleError && (
								<div style={{ color: "#ff4d4f", fontSize: 12, marginTop: 4 }}>
									Please enter title
								</div>
							)}
						</div>
					</Col>

					<Col span={8}>
						<Form.Item
							label={
								<>
									Level{" "}
									<Popover
										content={
											<>
												General announcements work as usual and can be closed once
												<br /> read. However, "Important" announcements cannot be closed or
												<br /> removed and will always be available for employees to view.
											</>
										}
										arrow={true}
									>
										<Info sx={{ color: "#2D53DA", fontSize: "20px", margin: "0px 8px", cursor: "pointer" }} />
									</Popover>
								</>
							}
							name="level"
						>
							<Select
								options={[
									{ label: "General", value: "General" },
									{ label: "Important", value: "Important" },
								]}
							/>
						</Form.Item>
					</Col>
				</Row>

				{/* ── Description editor ──────────────────────────────────────── */}
				<div style={{ marginBottom: 16 }}>
					<label style={{ fontWeight: 600, color: "#5d5f68", display: "block", marginBottom: 8 }}>
						Announcement description
					</label>
					<EditorWrapper>
						<EditorToolbar>
							<ToolbarLeft>
								<FontSizeControl>
									<ToolbarBtn
										onMouseDown={(e) => e.preventDefault()}
										style={{ width: 22 }}
										onClick={handleFontSizeDown}
										disabled={fontSize === 0}
									>−</ToolbarBtn>
									<span style={{ minWidth: 28, textAlign: "center", fontSize: 13 }}>
										{FONT_SIZES[fontSize]}
									</span>
									<ToolbarBtn
										onMouseDown={(e) => e.preventDefault()}
										style={{ width: 22 }}
										onClick={handleFontSizeUp}
										disabled={fontSize === FONT_SIZES.length - 1}
									>+</ToolbarBtn>
								</FontSizeControl>

								<ToolbarDivider />

								<ToolbarBtn onMouseDown={(e) => e.preventDefault()} style={{ fontWeight: 700, fontSize: 14 }} onClick={() => handleFormat("bold")}>B</ToolbarBtn>
								<ToolbarBtn onMouseDown={(e) => e.preventDefault()} style={{ textDecoration: "underline", fontSize: 14 }} onClick={() => handleFormat("underline")}>U</ToolbarBtn>
								<ToolbarBtn onMouseDown={(e) => e.preventDefault()} style={{ fontStyle: "italic", fontSize: 14 }} onClick={() => handleFormat("italic")}>I</ToolbarBtn>

								<ToolbarDivider />

								<ToolbarBtn onMouseDown={(e) => e.preventDefault()} title="Ordered list" onClick={() => handleFormat("list", "ordered")}><OlIcon /></ToolbarBtn>
								<ToolbarBtn onMouseDown={(e) => e.preventDefault()} title="Bullet list" onClick={() => handleFormat("list", "bullet")}><UlIcon /></ToolbarBtn>
							</ToolbarLeft>

							<Dropdown menu={{ items: dropdownItems }} placement="bottomRight" overlayStyle={{ minWidth: 180 }} trigger={["click"]}>
								<AttachBtn onClick={(e) => e.preventDefault()}>
									<PaperclipIcon />
									Attach File
									<DownOutlined style={{ fontSize: 10 }} />
								</AttachBtn>
							</Dropdown>
						</EditorToolbar>

						<ReactQuill
							ref={quillRef}
							theme="snow"
							value={description}
							onChange={(val) => setDescription(val)}
							modules={{ toolbar: false }}
							formats={["size", "bold", "underline", "italic", "list", "bullet"]}
							placeholder="Enter announcement details here..."
						/>

						{attachedFiles?.length > 0 && (
							<div style={{ padding: "10px 14px", borderTop: "1px solid #f0f0f0", display: "flex", flexWrap: "wrap", gap: 8, background: "#fafafa" }}>
								{attachedFiles.map((fileEntry) => (
									<FileChip key={fileEntry.uid}>
										<FileChipName
											loading={previewLoading === fileEntry.uid}
											title={fileEntry.name}
											onClick={() => previewLoading === fileEntry.uid ? null : handlePreviewFile(fileEntry)}
										>
											{fileEntry.name}
										</FileChipName>
										<FileChipRemove onClick={() => removeFile(fileEntry.uid)}>×</FileChipRemove>
									</FileChip>
								))}
							</div>
						)}
					</EditorWrapper>
				</div>

				{/* ── Send to ─────────────────────────────────────────────────── */}
				<Form.Item label="Send announcement to" name="sendTo" style={{ marginTop: 16 }}>
					<>
						<Input
							style={{ width: "250px" }}
							readOnly
							value={sendType === "specific" ? "Specific Employees" : "All Employees"}
							suffix={
								<EditOutlined
									style={{ color: "#5c5f5e", cursor: "pointer", background: "#C2FAE5", padding: "2px", borderRadius: "4px", height: "20px", width: "20px" }}
									onClick={() => setAnnouncementModalOpen(true)}
								/>
							}
							onClick={() => setAnnouncementModalOpen(true)}
							placeholder="All Employees"
						/>
						{sendType === "specific" && selectedSummary?.length > 0 && (
							<div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: "20px" }}>
								{selectedSummary.map((item, index) => (
									<div key={index} style={{ padding: "4px 10px", background: "#f0f2f5", borderRadius: "2px", fontSize: "12px", border: "1px solid #e6e6e6", display: "flex", alignItems: "center", gap: 6 }}>
										{item}
										<span
											style={{ cursor: "pointer", color: "#8c8c8c", fontWeight: 700, fontSize: 13, lineHeight: 1 }}
											onClick={() => handleRemoveSummaryItem(item)}
										>×</span>
									</div>
								))}
							</div>
						)}
					</>
				</Form.Item>

				{/* ── Footer ──────────────────────────────────────────────────── */}
				<FixedFooter sidebarWidth={290}>
					<Button style={{ borderRadius: 8 }} onClick={() => history.goBack()} disabled={loadingType !== null}>
						Cancel
					</Button>

					{isEditMode ? (
						<Button
							style={{ borderRadius: 8 }}
							type="primary"
							loading={loadingType === "publish" || loadingType === "email"}
							disabled={loadingType !== null}
							onClick={handleUpdateClick}
						>
							Update & Republish
						</Button>
					) : (
						<>
							<Button style={{ borderRadius: 8 }} type="default" loading={loadingType === "draft"} disabled={loadingType !== null} onClick={handleDraft}>
								Save as Draft
							</Button>
							<Button style={{ borderRadius: 8 }} type="primary" ghost loading={loadingType === "email"} disabled={loadingType !== null} onClick={handlePublishAndEmail}>
								Publish & Email
							</Button>
							<Button style={{ borderRadius: 8 }} type="primary" loading={loadingType === "publish"} disabled={loadingType !== null} onClick={() => publishAnnouncement(false, "Published")}>
								Publish
							</Button>
						</>
					)}
				</FixedFooter>
			</Form>

			<SendAnnouncementModal
				open={announcementModalOpen}
				roleList={roleOptions}
				locationList={locationList}
				onClose={() => setAnnouncementModalOpen(false)}
				onConfirmSelection={(data) => {
					setSelectedSummary(data.items || []);
					setSendType(data.sendType);
					setSelectedRoleIds(data.roleIds || []);
					setSelectedLocationIds(data.locationIds || []);
					setAnnouncementModalOpen(false);
				}}
				initialSendType={sendType === "specific" ? "Specific" : "All"}
				initialRoleIds={selectedRoleIds}
				initialLocationIds={selectedLocationIds}
			/>
		</Wrapper>
	);
};

export default AddEditAnnouncement;